// 우측 결과 패널 — 스크리닝 대형 결과 와이드 테이블 (바닐라 renderResultsPanel 이식,
// Phase 3-B). 정렬·검색·엑셀식 컬럼 필터·결과 세트 전환·엑셀 보기/다운로드·
// 좌측 엣지 너비 드래그(va_rp_w)·행 호버 퀵 액션 팝오버(rp-pop).
// 통합 우측 패널의 '스크리닝 결과' 탭 내용 — 탭 바(tabs)는 ChatPage 가 주입한다.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { libDownload } from "../../lib/library";
import { SidePanel, PanelClose } from "../../ui/SidePanel";
import { Button } from "../../ui/Button";

/** screen_panel 아티팩트 스펙 — 서버 계약(rows/columns/total/num_from/excel_*) 그대로.
 *  num_cols 는 ds_query 자유형 패널용 — 있으면 num_from 대신 이 인덱스들만 숫자 컬럼. */
export interface ScreenSpec {
  title?: string;
  columns?: string[];
  rows?: Array<Array<string | number | null>>;
  total?: number;
  num_from?: number;
  num_cols?: number[];
  excel_url?: string;
  excel_label?: string;
  [k: string]: unknown;
}

interface Props {
  specs: ScreenSpec[];
  idx: number;
  onPickIdx: (i: number) => void;
  onClose: () => void;
  /** 팝오버 퀵 액션 → 채팅 전송 */
  onAsk: (q: string) => void;
  busy: boolean;
  /** 너비 드래그 중 상태 — .chat-root.rp-resizing (커서·가이드라인) */
  onResizingChange: (on: boolean) => void;
  /** '엑셀 바로 보기' → 통합 패널의 파일 미리보기 탭으로 (다운로드 버튼과 별개) */
  onExcelPreview: (url: string, name: string) => void;
  /** 통합 패널 탭 바 — 결과·미리보기 둘 다 있을 때만 ChatPage 가 내려준다 */
  tabs?: ReactNode;
}

interface RpFilters {
  region: string;
  listed: string;
  audit: string;
}

const F_ALL: RpFilters = { region: "all", listed: "all", audit: "all" };

// 지역 값 — '지역' 컬럼이 있으면 그대로, 없으면 주소(마지막 컬럼) 첫 토큰.
// 둘 다 없으면(ds_query 자유형 패널) 지역 필터를 아예 숨긴다 — 숫자 컬럼이
// 지역 드롭다운으로 둔갑하는 사고 방지.
function regionOf(r: Array<string | number | null>, cols: string[]): string {
  const ri = cols.indexOf("지역");
  if (ri >= 0) return String(r[ri] || "");
  if (!String(cols[cols.length - 1] || "").includes("주소")) return "";
  return String(r[cols.length - 1] || "").trim().split(" ")[0] || "";
}

export default function ResultsPanel({
  specs,
  idx,
  onPickIdx,
  onClose,
  onAsk,
  busy,
  onResizingChange,
  onExcelPreview,
  tabs,
}: Props) {
  const spec = specs[idx] || null;
  const [sort, setSort] = useState<{ idx: number; dir: number }>({ idx: -1, dir: -1 });
  const [query, setQuery] = useState("");
  const [f, setF] = useState<RpFilters>(F_ALL);
  const [tools, setTools] = useState<"" | "filters-open" | "search-open">("");
  const panelRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);

  // 결과 세트를 바꾸면 정렬·검색·필터 초기화 (바닐라 _rpSelect)
  useEffect(() => {
    setSort({ idx: -1, dir: -1 });
    setQuery("");
    setF(F_ALL);
  }, [idx, specs]);

  // 아이콘 토글(필터/검색) — 밖 클릭 시 닫힘
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (toolsRef.current && !(e.target instanceof Node && toolsRef.current.contains(e.target)))
        setTools("");
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // ── 행 호버 퀵 액션 팝오버 ──
  const [pop, setPop] = useState<{
    name: string;
    sym: string;
    listed: boolean;
    top: number;
    left: number;
  } | null>(null);
  const popTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hidePop = () => setPop(null);
  const scheduleHidePop = () => {
    clearTimeout(popTimer.current);
    popTimer.current = setTimeout(hidePop, 180);
  };
  useEffect(() => {
    const onScroll = () => hidePop(); // 패널 스크롤 시 위치가 어긋나므로 닫는다
    document.addEventListener("scroll", onScroll, true);
    return () => document.removeEventListener("scroll", onScroll, true);
  }, []);

  const cols = useMemo(() => spec?.columns || [], [spec]);
  const all0 = useMemo(() => spec?.rows || [], [spec]);
  // 숫자 컬럼 — num_cols(자유형 패널)가 있으면 그 인덱스만,
  // 없으면 스크리너 계약대로 num_from 부터 마지막(주소) 직전까지
  const isNum = (i: number) =>
    spec
      ? Array.isArray(spec.num_cols)
        ? spec.num_cols.includes(i)
        : i >= (spec.num_from ?? 6) && i < cols.length - 1
      : false;
  // 배지·필터 컬럼은 이름으로 찾는다 — 스크리너 패널은 2·3에 고정이지만
  // ds_query 자유형 패널엔 외감 컬럼이 없다 (이름 기반이라 둘 다 동작)
  const listedIdx = cols.indexOf("구분");
  const auditIdx = cols.indexOf("외감");

  // 엑셀식 컬럼 필터 — 데이터에 실제 존재하는 값으로 드롭다운 구성
  const regs = useMemo(
    () => Array.from(new Set(all0.map((r) => regionOf(r, cols)))).filter(Boolean).sort(),
    [all0, cols],
  );
  const listedVals = useMemo(
    () => (listedIdx < 0 ? [] : (Array.from(new Set(all0.map((r) => r[listedIdx]))).filter(Boolean) as string[])),
    [all0, listedIdx],
  );
  const auditVals = useMemo(
    () => (auditIdx < 0 ? [] : (Array.from(new Set(all0.map((r) => r[auditIdx]))).filter(Boolean) as string[])),
    [all0, auditIdx],
  );

  if (!spec) return null;
  const total = spec.total || all0.length;

  const q = query.trim().toLowerCase();
  let rows = all0.slice();
  if (f.region !== "all") rows = rows.filter((r) => regionOf(r, cols) === f.region);
  if (f.listed !== "all" && listedIdx >= 0) rows = rows.filter((r) => r[listedIdx] === f.listed);
  if (f.audit !== "all" && auditIdx >= 0) rows = rows.filter((r) => r[auditIdx] === f.audit);
  if (q) rows = rows.filter((r) => r.join(" ").toLowerCase().includes(q));
  if (sort.idx >= 0) {
    const i = sort.idx, dir = sort.dir, num = isNum(i);
    rows.sort((ra, rb) => {
      const av = ra[i], bv = rb[i];
      if (num) {
        const an = typeof av === "number" ? av : null;
        const bn = typeof bv === "number" ? bv : null;
        if (an === null && bn === null) return 0;
        if (an === null) return 1; // 비공개는 정렬 방향과 무관하게 아래로
        if (bn === null) return -1;
        return (an - bn) * dir;
      }
      return String(av || "").localeCompare(String(bv || ""), "ko") * dir;
    });
  }

  const filterOn = f.region !== "all" || f.listed !== "all" || f.audit !== "all";
  const sub =
    (rows.length !== all0.length ? `필터 ${rows.length.toLocaleString()}개 표시 / ` : "") +
    `${total.toLocaleString()}개 기업` +
    (total > all0.length ? ` — 패널엔 ${all0.length.toLocaleString()}개, 전체는 엑셀` : "");

  const onRowOver = (e: React.MouseEvent) => {
    const tr = (e.target as HTMLElement).closest("tbody tr") as HTMLTableRowElement | null;
    if (!tr || !tr.dataset.name) return;
    clearTimeout(popTimer.current);
    const rect = tr.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    setPop({
      name: tr.dataset.name,
      sym: tr.dataset.sym || "",
      listed: tr.dataset.listed === "1",
      top: Math.max(60, Math.min(window.innerHeight - 160, rect.top + rect.height / 2)),
      left: Math.max(12, (panel ? panel.right : window.innerWidth) - 210 - 26),
    });
  };

  // 심볼을 함께 보내 모델이 기업명 재조회(resolve) 없이 바로 조회하게 한다
  const popActs = pop
    ? (() => {
        const who = pop.sym ? `${pop.name}(${pop.sym})` : pop.name;
        return [
          { label: "기업 상세 조회", q: `${who} 기업 상세 정보 조회해줘` },
          { label: "최근 3년 재무데이터 조회", q: `${who} 최근 3년 재무데이터 조회해줘` },
          pop.listed
            ? { label: "최근 주가 조회", q: `${who} 최근 주가 알려줘` }
            : { label: "업황 조회", q: `${who} 업황 알려줘` },
        ];
      })()
    : [];

  return (
    <SidePanel
      label="결과 패널"
      ref={panelRef}
      tabs={tabs}
      titles={
        <>
          {/* 결과 세트가 여러 개면 제목 대신 선택 드롭다운 */}
          {specs.length > 1 ? (
            <select
              className="rp-picker"
              aria-label="결과 세트 선택"
              value={idx}
              onChange={(e) => onPickIdx(Number(e.target.value))}
            >
              {specs.map((s, i) => (
                <option value={i} key={i}>
                  {s.title || "결과 " + (i + 1)}
                </option>
              ))}
            </select>
          ) : (
            <div className="rp-title">{spec.title || "스크리닝 결과"}</div>
          )}
          <div className="rp-sub">{sub}</div>
        </>
      }
      actions={<PanelClose label="결과 패널 닫기" onClick={onClose} />}
      onResizingChange={onResizingChange}
    >
      <div className={`rp-tools${tools ? " " + tools : ""}`} ref={toolsRef}>
        {/* 좁은 폭에서는 아이콘 토글 → 툴팁형 필터/검색 (컨테이너 쿼리) */}
        <Button
          variant="icon"
          className={`rp-ftoggle${filterOn ? " on" : ""}`}
          title="필터"
          aria-label="필터"
          onClick={() => setTools((t) => (t === "filters-open" ? "" : "filters-open"))}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 5h18l-7 8v5l-4 2v-7z" />
          </svg>
          <span className="rp-fdot" />
        </Button>
        <div className="rp-colfilters">
          {/* 필터는 데이터에 실제로 값이 갈리는 컬럼만 */}
          {regs.length > 1 && (
            <select
              className="rp-cf"
              aria-label="지역 필터"
              value={f.region}
              onChange={(e) => setF((p) => ({ ...p, region: e.target.value }))}
            >
              <option value="all">지역: 전체</option>
              {regs.map((o) => (
                <option value={o} key={o}>
                  {o}
                </option>
              ))}
            </select>
          )}
          {listedVals.length > 1 && (
            <select
              className="rp-cf"
              aria-label="구분 필터"
              value={f.listed}
              onChange={(e) => setF((p) => ({ ...p, listed: e.target.value }))}
            >
              <option value="all">구분: 전체</option>
              {listedVals.map((o) => (
                <option value={o} key={o}>
                  {o}
                </option>
              ))}
            </select>
          )}
          {auditVals.length > 1 && (
            <select
              className="rp-cf"
              aria-label="외감 필터"
              value={f.audit}
              onChange={(e) => setF((p) => ({ ...p, audit: e.target.value }))}
            >
              <option value="all">외감: 전체</option>
              {auditVals.map((o) => (
                <option value={o} key={o}>
                  {o}
                </option>
              ))}
            </select>
          )}
        </div>
        <Button
          variant="icon"
          className="rp-stoggle"
          title="검색"
          aria-label="검색"
          onClick={() => {
            setTools((t) => {
              const next = t === "search-open" ? "" : "search-open";
              if (next) setTimeout(() => searchRef.current?.focus(), 0);
              return next;
            });
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
        </Button>
        <input
          className="rp-search"
          ref={searchRef}
          type="search"
          placeholder="기업명·업종·주소 검색"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {spec.excel_url && (
          <div className="rp-file">
            <button
              className="rp-excel"
              type="button"
              title="엑셀 바로 보기"
              onClick={() =>
                spec.excel_url &&
                onExcelPreview(spec.excel_url, spec.excel_label || "screening.xlsx")
              }
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span className="rp-btn-txt">엑셀 바로 보기</span>
            </button>
            <button
              className="rp-excel"
              type="button"
              title="엑셀 다운로드"
              onClick={() =>
                spec.excel_url && void libDownload(spec.excel_url, spec.excel_label || "screening.xlsx")
              }
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              <span className="rp-btn-txt">다운로드</span>
            </button>
          </div>
        )}
      </div>
      <div className="rp-scroll">
        <table
          className="rp-table"
          onMouseOver={onRowOver}
          onMouseLeave={scheduleHidePop}
          onClick={(e) => {
            const th = (e.target as HTMLElement).closest("th[data-ci]") as HTMLElement | null;
            if (!th) return;
            const i = Number(th.dataset.ci);
            setSort((s) =>
              s.idx === i ? { idx: i, dir: -s.dir } : { idx: i, dir: isNum(i) ? -1 : 1 },
            );
          }}
        >
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th data-ci={i} key={i}>
                  {c}
                  {sort.idx === i && <span className="rp-dir">{sort.dir > 0 ? "▲" : "▼"}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr
                key={ri}
                data-name={String(r[0] || "")}
                data-sym={String(r[1] || "")}
                data-listed={(listedIdx >= 0 ? r[listedIdx] : r[2]) === "상장" ? 1 : 0}
              >
                {r.map((v, i) => {
                  if (i === listedIdx)
                    return (
                      <td key={i}>
                        <span className={`rp-badge${v === "상장" ? " krx" : ""}`}>{v || ""}</span>
                      </td>
                    );
                  if (i === auditIdx)
                    return (
                      <td key={i}>
                        <span className={`rp-badge${v === "외감" ? " audit" : ""}`}>{v || ""}</span>
                      </td>
                    );
                  if (isNum(i))
                    return typeof v === "number" ? (
                      <td className="num" key={i}>
                        {v.toLocaleString("ko-KR")}
                      </td>
                    ) : (
                      <td className="num muted" key={i}>
                        비공개
                      </td>
                    );
                  return <td key={i}>{v == null ? "" : v}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* 행 호버 퀵 액션 팝오버 — 툴팁처럼 행 옆에 떠서 바로 채팅 질문 전송 */}
      {pop && (
        <div
          className="rp-pop"
          style={{ top: pop.top, left: pop.left }}
          onMouseEnter={() => clearTimeout(popTimer.current)}
          onMouseLeave={scheduleHidePop}
        >
          <div className="rp-pop-head">{pop.name}</div>
          {popActs.map((a) => (
            <button
              type="button"
              className="rp-pop-btn"
              key={a.label}
              onClick={() => {
                hidePop();
                if (!busy) onAsk(a.q);
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </SidePanel>
  );
}
